import React from 'react';
import { createDirectMessage } from '../data/chat';
import { useToasts } from '../ui/ToastProvider';

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

/**
 * Opens (or creates) a direct-message conversation with a Portal profile and
 * shows it in the chat panel. Used by Schedule's team member card and the
 * dotboard. `beforeOpen` runs after the conversation exists and before the
 * panel opens, so callers can close their own dialog first.
 */
export function useOpenDirectChat() {
  const { push } = useToasts();
  const [opening, setOpening] = React.useState(false);

  const openDirectChat = React.useCallback(async function openDirectChatWith(profileId: string, beforeOpen?: () => void) {
    setOpening(true);
    try {
      const { data, error } = await createDirectMessage(profileId);
      if (error) throw error;
      if (!data?.conversation_id) throw new Error('Unable to open chat.');
      beforeOpen?.();
      window.dispatchEvent(new CustomEvent('chat:open-conversation', {
        detail: { conversationId: data.conversation_id, openPanel: true },
      }));
      return true;
    } catch (err: unknown) {
      push({ type: 'error', message: errorMessage(err, 'Failed to open chat.') });
      return false;
    } finally {
      setOpening(false);
    }
  }, [push]);

  return { openDirectChat, opening };
}
